import React from 'react';
import { Link } from 'react-router-dom';
import {
  StyledPostTitle,
  StyledParagraph,
  StyledSectionHeading,
  StyledOL,
} from '../styled';

export default function Article() {
  return (
    <>
      <StyledPostTitle id="football-manager-with-friends">
        Football manager with friends: run a club with your group, not a
        lonely save
      </StyledPostTitle>
      <StyledParagraph big>
        Searching for a football manager with friends usually lands you in
        one of two places: how-tos for hosting Football Manager Online Career
        with mates who all own the game, or solo browser managers that barely
        mention your group. The real ask is simpler — a social football
        manager game your Discord or friend circle can finish together without
        syncing a heavy save.
      </StyledParagraph>
      <StyledParagraph big>
        The Open League (TOL) is built for that: free short seasons, Discord
        match nights, and human transfers. Claim clubs, talk deals in chat,
        and watch highlights with people you already know — on phone or web,
        no Steam or Netflix wall required.
      </StyledParagraph>

      <StyledSectionHeading>
        What “football manager with friends” usually means
      </StyledSectionHeading>
      <StyledParagraph big>
        The phrase covers a few different products that feel social on paper:
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            FM Online Career / console friends-only — deep official multiplayer,
            but everyone needs the paid client, the same version, and a schedule
            that can survive a long save.
          </li>
          <li>
            Discord pack / card bots — fast and chatty inside any server;
            often collectible-first rather than a shared league table.
          </li>
          <li>
            Browser multiplayer managers — leagues with humans; quality and
            season length vary a lot.
          </li>
          <li>
            The Open League — short multiplayer seasons, Discord match nights,
            and deals with real managers. Built so a group can play football
            manager with friends online and finish a story together.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        Honest contrast, not a ranked list — these are different sports:
      </StyledParagraph>
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'Archivo, sans-serif',
            fontWeight: 300,
            fontSize: 16,
            lineHeight: '26px',
            letterSpacing: '0.4px',
            textAlign: 'left',
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #000' }}>
                Option
              </th>
              <th style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #000' }}>
                How you play
              </th>
              <th style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #000' }}>
                Friction
              </th>
              <th style={{ padding: '8px 0', borderBottom: '1px solid #000' }}>
                Best for
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                FM Online
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Official multiplayer save
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Paid client, sync, long commits
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                Deep FM with mates who all own it
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Discord bots
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Slash commands / packs in chat
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Light league depth
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                Fast social play inside any server
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Browser F2P
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Web leagues, often solo grind
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Ads / timers common
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                Casual daily taps
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                The Open League
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Shared league + Discord match night
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                None — free, web / phone
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                Friend groups who want a finishable season
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <StyledParagraph big>
        FM remains the deepest single-player football management experience.
        TOL isn’t trying to replace that depth — it’s the social football
        manager game lane for groups who want people, not another 400-hour
        save.
      </StyledParagraph>

      <StyledSectionHeading>
        Why friend groups bounce off classic FM multiplayer
      </StyledSectionHeading>
      <StyledParagraph big>
        Classic FM multiplayer is excellent when the whole squad already owns
        the game and can protect matchdays. Friend groups bounce for ordinary
        reasons:
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Everyone needs the paid client and the same version — one mate on
            an older build breaks the night.
          </li>
          <li>
            Waiting on processors and mismatched availability turns “play with
            friends” into calendar Tetris.
          </li>
          <li>
            A 400-hour save vs “can we finish a season this month?” — most
            Discord circles want a story with an ending.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        If the barrier is price or a free browser path, see{' '}
        <Link to="/blog/free-football-manager-online">
          free football manager online
        </Link>
        . If the barrier is device — including phone play without a streaming
        plan — see{' '}
        <Link to="/blog/play-football-manager-on-phone">
          play football manager on phone
        </Link>
        .
      </StyledParagraph>

      <StyledSectionHeading>
        How The Open League works as a football manager with friends
      </StyledSectionHeading>
      <StyledParagraph big>
        TOL is free to play. You claim clubs in a shared league, set an XI,
        and compete against people — including the soccer-curious friends who
        never bought FM. The loop is built for groups:
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Short seasons (~three weeks) — promote, relegate, then{' '}
            <Link to="/user-guide/off-season">youth camp</Link> and deals in
            the break. Finish a story together.
          </li>
          <li>
            Daily sims + text highlights where the community already chats
            (Discord).
          </li>
          <li>
            Transfers with real humans, not only AI boards — negotiate in chat,
            formalize in the club portal.
          </li>
          <li>
            Simple tactics — formations, effort, mindset. Easy to set; hard
            against people.
          </li>
          <li>
            Web portal + phone-friendly play — no Steam client, no Netflix
            install wall.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        Competition comes from your group, not from padding menus. That’s what
        “play football manager with friends online” looks like when the product
        assumes humans from day one.
      </StyledParagraph>

      <StyledSectionHeading>
        Discord servers vs random matchmaking
      </StyledSectionHeading>
      <StyledParagraph big>
        Random queues are fine for a quick match. Friend leagues are better
        when you already have a server, a timezone, and banter. Invite your
        existing community, claim clubs that fit your evenings, and treat match
        night as social content — negotiations, shared highlights, and table
        talk — not just menus.
      </StyledParagraph>
      <StyledParagraph big>
        For the Discord-native lane in more detail, see{' '}
        <Link to="/blog/discord-football-manager">
          Discord football manager
        </Link>
        . TOL is a full shared league with a club portal and scheduled sims —
        complementary to slash-command bots, not another pack bot.
      </StyledParagraph>

      <StyledSectionHeading>
        How to start a friends league tonight
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Register at <Link to="/">theopenleague.com</Link> and join or claim
            a club in a timezone that matches your group.
          </li>
          <li>
            Skim{' '}
            <Link to="/user-guide/start">Getting started</Link>,{' '}
            <Link to="/user-guide/coaching">set your XI</Link>, and you’re ready
            for the first sim.
          </li>
          <li>
            Pull mates into Discord for match night — that’s where highlights
            and deals live.
          </li>
          <li>
            Community admins: the{' '}
            <Link to="/press-kit">press kit</Link> has assets if you want to
            announce the league in your server.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        More reading on the{' '}
        <Link to="/blog">blog</Link> if you want the wider genre map before
        you invite the group.
      </StyledParagraph>

      <StyledSectionHeading>FAQ</StyledSectionHeading>
      <StyledParagraph big>
        <strong>
          Can we play a football manager with friends for free?
        </strong>{' '}
        Yes — The Open League is free to play. Optional convenience exists; the
        friend-group loop isn’t gated.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>
          Do my friends need Steam, Netflix, or a Discord bot invite only?
        </strong>{' '}
        No Steam or Netflix required. Manage via the web portal on phone or
        desktop. Discord is where match highlights, chat, and negotiations
        live — see{' '}
        <Link to="/blog/discord-football-manager">
          Discord football manager
        </Link>
        .
      </StyledParagraph>
      <StyledParagraph big>
        <strong>
          How is this different from Football Manager Online Career?
        </strong>{' '}
        FM Online is the deep official multiplayer save. TOL is a shorter
        social football manager game built for groups who want seasons you can
        finish together — not a replacement for FM’s simulation depth.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Is this the same as Discord football manager bots?</strong>{' '}
        Bots are great for slash-command / pack play inside any server. TOL is
        a full shared league with a club portal, scheduled sims, and human
        transfers — complementary, not identical.
      </StyledParagraph>

      <StyledParagraph big>
        Ready to manage with your mates?{' '}
        <Link to="/">Play The Open League</Link>
        {' — then catch Discord match night. '}
        <Link to="/user-guide/start">User guide</Link>
        {' — '}
        <Link to="/blog/discord-football-manager">
          Discord football manager
        </Link>
        .
      </StyledParagraph>
    </>
  );
}
